import type { LandingWithBlocks } from "../hooks/useLandings";
import type { Database } from "../lib/supabase";
import { trackEvent } from "../lib/analytics";
import { getLocalizedValue, type SupportedLocale } from "../lib/localization";
import { PublicContactForm } from "./PublicContactForm";
import { sanitizeHtml } from "../lib/sanitizeHtml";
import type { CSSProperties } from "react";
import { PUBLIC_THEME_DEFAULTS, themeStyle } from "../lib/websiteDefaults";
import { SITE_LOGO } from "./public/SiteNav";

// ON45D Studio brand, same tokens as the public site (/site): ink background, cyan accent, muted teal-grey text.
const ACCENT = "var(--public-accent)";
const MUTED = "var(--public-muted)";
const CARD_BG = "#0F1A1A";
const CTA_BG = "#0F2827";
const HERO_GLOW = "radial-gradient(60% 50% at 50% 0%, rgba(40, 241, 231, 0.14) 0%, rgba(11, 15, 15, 0) 100%)";
const CARD = "rounded-2xl border border-white/10 bg-[#0F1A1A] p-6";
const ALT_BG = "bg-white/[0.02]";

type LandingBlock = Database['public']['Tables']['landing_blocks']['Row'];

interface LandingPagePreviewProps {
  landing: LandingWithBlocks | null;
  previewBlocks?: LandingBlock[];
  locale?: SupportedLocale;
}

export function LandingPagePreview({
  landing,
  previewBlocks,
  locale = "es",
}: LandingPagePreviewProps) {
  const tr = (en: string, es: string) => (locale === "es" ? es : en);

  if (!landing) {
    return (
      <div className="flex h-96 items-center justify-center rounded-xl border border-white/10 bg-surface-1">
        <p className="text-paper/55">{tr("No landing page selected", "No hay landing page seleccionada")}</p>
      </div>
    );
  }

  const blocks = previewBlocks || landing.landing_blocks || [];
  const quoteAnchor = "quote-request";
  const hasFaqBlock = blocks.some((block) => block.type === "FAQs");

  const localized = <T,>(value: unknown): T | undefined => getLocalizedValue<T>(value, locale);
  const text = (value: unknown, fallback: string) => localized<string>(value) || fallback;

  const ctaHref = (value: unknown, fallback = `#${quoteAnchor}`) => {
    const url = localized<string>(value);
    if (!url || !url.trim()) return fallback;
    return url;
  };

  const handleCTA = (label: string, destination: string) => {
    trackEvent("landing_cta_click", {
      landing: landing.url,
      locale,
      label,
      destination,
    });
  };

  const fallbackFaqItems = [
    {
      question: tr("How fast can this project launch?", "¿En cuánto tiempo pueden lanzar este proyecto?"),
      answer: tr(
        "Usually between 2 and 6 weeks depending on scope and available assets.",
        "Entre 2 y 6 semanas según alcance y materiales disponibles."
      ),
    },
    {
      question: tr(
        "Do you cover both design and development?",
        "¿Incluyen diseño y desarrollo en el mismo equipo?"
      ),
      answer: tr(
        "Yes, we cover strategy, UX/UI and development to keep quality and speed aligned.",
        "Si, cubrimos estrategia, UX/UI y desarrollo para mantener coherencia y velocidad."
      ),
    },
    {
      question: tr("How do I request a tailored quote?", "¿Cómo solicito una cotización personalizada?"),
      answer: tr(
        "Use the final form and we will send a clear proposal in less than 24 hours.",
        "Usa el formulario final y te enviaremos una propuesta clara en menos de 24 horas."
      ),
    },
  ];

  const renderBlock = (block: LandingBlock) => {
    const content = block.content as any;
    const list = <T,>(value: unknown): T[] => {
      const picked = localized<T[]>(value);
      if (Array.isArray(picked)) return picked;
      return Array.isArray(value) ? (value as T[]) : [];
    };

    switch (block.type) {
      case "Hero":
        return (
          <section
            key={block.id}
            className="relative overflow-hidden py-20 sm:py-28"
            style={{ background: HERO_GLOW }}
          >
            <div className="site-container">
              <div className="mx-auto max-w-3xl text-center">
                <span className="site-badge">ON45D Studio</span>
                <h1 className="site-h1 mt-4 mb-5 text-paper">
                  {text(content.title, tr("Hero title", "Título principal"))}
                </h1>
                {(content.subtitle || content.description) && (
                  <p className="site-lead mx-auto mb-8 max-w-2xl" style={{ color: MUTED }}>
                    {text(content.subtitle || content.description, "")}
                  </p>
                )}
                {content.ctaText && (
                  <a
                    href={ctaHref(content.ctaUrl)}
                    onClick={() => handleCTA(text(content.ctaText, tr("Request quote", "Solicitar cotización")), ctaHref(content.ctaUrl))}
                    className="site-btn"
                  >
                    {text(content.ctaText, tr("Request quote", "Solicitar cotización"))}
                  </a>
                )}
                {content.image && (
                  <div className="mt-8">
                    <img
                      src={content.image}
                      alt={text(content.title, landing.name)}
                      className="mx-auto rounded-3xl border border-white/10"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        );

      case "Features":
        return (
          <section key={block.id} className="py-16 sm:py-20">
            <div className="site-container">
              {content.title && (
                <h2 className="site-h2 mb-12 text-center text-paper">
                  {text(content.title, "")}
                </h2>
              )}
              <div className="grid gap-8 md:grid-cols-3">
                {list<any>(content.items).map((item: any, index: number) => (
                  <div
                    key={index}
                    className={CARD}
                  >
                    {item.icon && (
                      <div className="mb-4 text-4xl">{item.icon}</div>
                    )}
                    <h3 className="mb-2 text-xl font-bold text-paper">
                      {text(item.title || item.name, "")}
                    </h3>
                    {item.description && (
                      <p className="site-body" style={{ color: MUTED }}>{text(item.description, "")}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case "Pricing":
        return (
          <section key={block.id} className={`${ALT_BG} py-20`}>
            <div className="site-container">
              {content.title && (
                <h2 className="site-h2 mb-12 text-center text-paper">
                  {text(content.title, "")}
                </h2>
              )}
              <div className="grid gap-8 md:grid-cols-3">
                {list<any>(content.plans).map((plan: any, index: number) => (
                  <div
                    key={index}
                    className={`${CARD} p-8`}
                  >
                    <h3 className="mb-2 text-2xl font-bold text-paper">
                      {text(plan.name, "")}
                    </h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold" style={{ color: ACCENT }}>
                        {plan.price}
                      </span>
                      {plan.period && (
                        <span style={{ color: MUTED }}>/{plan.period}</span>
                      )}
                    </div>
                    <ul className="mb-6 space-y-2">
                      {(plan.features || []).map((feature: string, fIndex: number) => (
                        <li key={fIndex} className="flex items-center gap-2" style={{ color: MUTED }}>
                          <span style={{ color: ACCENT }}>✓</span>
                          {text(feature, "")}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={ctaHref(plan.ctaUrl || content.ctaUrl)}
                      className="site-btn site-btn-sm w-full"
                      onClick={() =>
                        handleCTA(text(plan.ctaText, tr("Request quote", "Solicitar cotización")), ctaHref(plan.ctaUrl || content.ctaUrl))
                      }
                    >
                      {text(plan.ctaText, tr("Request quote", "Solicitar cotización"))}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case "Testimonials":
        return (
          <section key={block.id} className="py-20">
            <div className="site-container">
              {content.title && (
                <h2 className="site-h2 mb-12 text-center text-paper">
                  {text(content.title, "")}
                </h2>
              )}
              <div className="grid gap-8 md:grid-cols-2">
                {list<any>(content.testimonials).map((testimonial: any, index: number) => (
                  <div
                    key={index}
                    className={CARD}
                  >
                    <p className="site-lead mb-4 italic">"{text(testimonial.quote, "")}"</p>
                    <div className="flex items-center gap-4">
                      {testimonial.avatar && (
                        <img
                          src={testimonial.avatar}
                          alt={text(testimonial.name, tr("Client", "Cliente"))}
                          className="h-12 w-12 rounded-full"
                          loading="lazy"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-paper">{text(testimonial.name, "")}</p>
                        {testimonial.role && (
                          <p className="text-sm" style={{ color: MUTED }}>{text(testimonial.role, "")}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case "CTA":
        return (
          <section key={block.id} className="py-20">
            <div className="site-container">
              <div className="mx-auto max-w-4xl rounded-3xl border px-6 py-14 text-center sm:px-12" style={{ background: CTA_BG, borderColor: "rgba(40, 241, 231, 0.25)" }}>
              {content.title && (
                <h2 className="site-h2 mb-4 text-paper">{text(content.title, "")}</h2>
              )}
              {content.description && (
                <p className="site-lead mx-auto mb-8 max-w-2xl" style={{ color: MUTED }}>{text(content.description, "")}</p>
              )}
              {content.ctaText && (
                <a
                  href={ctaHref(content.ctaUrl)}
                  className="site-btn"
                  onClick={() => handleCTA(text(content.ctaText, tr("Request quote", "Solicitar cotización")), ctaHref(content.ctaUrl))}
                >
                  {text(content.ctaText, tr("Request quote", "Solicitar cotización"))}
                </a>
              )}
            </div>
            </div>
          </section>
        );

      case "Text":
        return (
          <section key={block.id} className="py-20">
            <div className="site-container">
             <div className="mx-auto max-w-3xl">
              {content.title && (
                <h2 className="site-h2 mb-6 text-paper">{text(content.title, "")}</h2>
              )}
              {content.body && (
                <div
                  className="site-post-body"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(content.body)) }}
                />
              )}
            </div>
            </div>
          </section>
        );

      case "FAQs":
        return (
          <section key={block.id} className={`${ALT_BG} py-20`}>
            <div className="site-container">
             <div className="mx-auto max-w-3xl">
              {content.title && (
                <h2 className="site-h2 mb-12 text-center text-paper">
                  {text(content.title, "")}
                </h2>
              )}
              <div className="space-y-4">
                {list<any>(content.items).map((faq: any, index: number) => (
                  <details
                    key={index}
                    className={CARD}
                  >
                    <summary className="cursor-pointer font-semibold text-paper">
                      {text(faq.question, `${tr("Question", "Pregunta")} ${index + 1}`)}
                    </summary>
                    <p className="site-body mt-4" style={{ color: MUTED }}>{text(faq.answer, tr("Answer here", "Respuesta aquí"))}</p>
                  </details>
                ))}
              </div>
            </div>
            </div>
          </section>
        );

      case "Comparison":
        return (
          <section key={block.id} className="py-20">
            <div className="site-container">
              {content.title && (
                <h2 className="site-h2 mb-12 text-center text-paper">
                  {text(content.title, "")}
                </h2>
              )}
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full border-collapse" style={{ background: CARD_BG }}>
                  <thead>
                    <tr className="bg-white/[0.04]">
                      <th className="border-b border-white/10 px-6 py-4 text-left text-sm font-semibold text-paper">
                        {tr("Feature", "Caracteristica")}
                      </th>
                      {list<any>(content.plans).map((plan: any, index: number) => (
                        <th
                          key={index}
                          className="border-b border-white/10 px-6 py-4 text-center text-sm font-semibold text-paper"
                        >
                          {text(plan.name, `Plan ${index + 1}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {list<string>(content.features).map((feature: string, fIndex: number) => (
                      <tr key={fIndex} className="border-b border-white/5">
                        <td className="px-6 py-4 text-sm" style={{ color: MUTED }}>{text(feature, "")}</td>
                        {list<any>(content.plans).map((plan: any, pIndex: number) => (
                          <td key={pIndex} className="px-6 py-4 text-center">
                            {plan.features && plan.features[fIndex] ? (
                              <span style={{ color: ACCENT }}>✓</span>
                            ) : (
                              <span className="text-white/25">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        );

      default:
        return (
          <section key={block.id} className="py-10">
            <div className="site-container">
              <div className={`${CARD} p-8 text-center`}>
                <p style={{ color: MUTED }}>{tr("Block type", "Tipo de bloque")}: {block.type}</p>
                <pre className="mt-4 text-left text-xs text-white/50">
                  {JSON.stringify(content, null, 2)}
                </pre>
              </div>
            </div>
          </section>
        );
    }
  };

  return (
    <div className="public-site min-h-screen" style={themeStyle(PUBLIC_THEME_DEFAULTS) as CSSProperties}>
      <header className="sticky top-0 z-50 px-3 pt-3 sm:px-6 sm:pt-4">
        <div className="mx-auto flex h-[57px] max-w-[920px] items-center justify-between rounded-[20px] pl-4 pr-2" style={{ background: CTA_BG }}>
          <img src={SITE_LOGO} alt="ON45D Studio" className="h-[30px] w-auto sm:h-[34px]" />
          <a
            href={`#${quoteAnchor}`}
            className="site-btn site-btn-sm"
            onClick={() => handleCTA(tr("Request quote", "Solicitar cotización"), `#${quoteAnchor}`)}
          >
            {tr("Request quote", "Solicitar cotización")}
          </a>
        </div>
      </header>

      {blocks.length > 0 ? (
        blocks.map((block) => renderBlock(block))
      ) : (
        <div className="flex h-96 items-center justify-center">
          <p style={{ color: MUTED }}>{tr("No blocks added yet. Add blocks to see preview.", "Aún no hay bloques. Agrega bloques para ver el preview.")}</p>
        </div>
      )}

      {!hasFaqBlock && (
        <section className={`${ALT_BG} py-20`}>
          <div className="site-container">
            <div className="mx-auto max-w-3xl">
              <h2 className="site-h2 mb-10 text-center text-paper">
                {tr("Frequently asked questions", "Preguntas frecuentes")}
              </h2>
              <div className="space-y-4">
                {fallbackFaqItems.map((faq, index) => (
                  <details key={index} className={CARD}>
                    <summary className="cursor-pointer font-semibold text-paper">{faq.question}</summary>
                    <p className="site-body mt-4" style={{ color: MUTED }}>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section id={quoteAnchor} className="py-20">
        <div className="site-container">
          <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 px-6 py-12 sm:px-12" style={{ background: CARD_BG }}>
            <span className="site-badge block text-center">{tr("Quote", "Cotización")}</span>
            <h2 className="site-h2 mt-3 text-center text-paper">
              {tr("Request a professional proposal", "Solicita una propuesta profesional")}
            </h2>
            <p className="site-body mt-3 text-center" style={{ color: MUTED }}>
              {tr(
                "Leave your contact details and tell us what you need. We reply within one business day.",
                "Déjanos tus datos de contacto y cuéntanos qué necesitas. Respondemos en un día hábil."
              )}
            </p>
            <div className="mt-8">
              <PublicContactForm
                locale={locale}
                source="landing"
                landingUrl={landing.url}
                submitLabel={tr("Submit request", "Enviar solicitud")}
                variant="dark"
                extraTracking={{ landing: landing.url }}
              />
            </div>
          </div>
        </div>
      </section>

      <footer className="py-10">
        <div className="site-container">
          <div className="site-divider-x mb-8" />
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <img src={SITE_LOGO} alt="ON45D Studio" className="h-[30px] w-auto" />
            <p className="text-sm" style={{ color: MUTED }}>© {new Date().getFullYear()} ON45D Studio</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
